import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import {
    type CropMonitoringNoteFilters,
    createCropMonitoringNote,
    deleteCropMonitoringNote,
    getCropMonitoringNoteById,
    getCropMonitoringNotes,
    updateCropMonitoringNote,
} from '/@/store/crop-monitoring-notes/apiProviders';
import type { CropMonitoringNoteInput } from '/@/types/crop-monitoring-note';
import { invalidate } from '/@/utils/swr';

const LIST_KEY = 'useCropMonitoringNotes';
const BY_ID_KEY = 'useCropMonitoringNoteById';

export const useCropMonitoringNotes = (filters: CropMonitoringNoteFilters = {}) => {
    const key = filters.field ? { key: LIST_KEY, ...filters } : null;
    return useSWR(key, () => getCropMonitoringNotes(filters), {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
};

export const useCropMonitoringNoteById = (id?: number) => {
    const key = id ? { key: BY_ID_KEY, id } : null;
    return useSWR(key, () => getCropMonitoringNoteById(id as number), {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
};

export const useMutateCreateCropMonitoringNote = () =>
    useSWRMutation(
        { key: 'createCropMonitoringNote' },
        (_key, { arg }: { arg: CropMonitoringNoteInput }) => createCropMonitoringNote(arg),
        { onSuccess: () => invalidate(LIST_KEY) }
    );

export const useMutateUpdateCropMonitoringNote = () =>
    useSWRMutation(
        { key: 'updateCropMonitoringNote' },
        (_key, { arg }: { arg: { id: number } & CropMonitoringNoteInput }) => {
            const { id, ...data } = arg;
            return updateCropMonitoringNote(id, data);
        },
        {
            onSuccess: () => {
                invalidate(LIST_KEY);
                invalidate(BY_ID_KEY);
            },
        }
    );

export const useMutateDeleteCropMonitoringNote = () =>
    useSWRMutation(
        { key: 'deleteCropMonitoringNote' },
        (_key, { arg }: { arg: { id: number } }) => deleteCropMonitoringNote(arg.id),
        { onSuccess: () => invalidate(LIST_KEY) }
    );
